import { fetchProductOffer } from '@/app/[lang]/[product]/[partner]/steps/fetchProductOfferAnalytics';
import { fetchProductConfig } from '@/server-actions/fetch-product-config';
import { queryCustomerSession } from '@/server-actions/query-customer-session';
import { trackEventsInFullStory } from '@/utils/fullStory-utils';
import { getUrlData } from '@/utils/urlData';
import { getPaths } from '@/utils/urlPaths';
import { ExpertSession } from '@asurion-core/asp-core';
import { useProductOffer } from '@asurion-core/asp-react-core';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import {
  buildEventProperties,
  EventPropertiesInput,
} from './buildAnalyticsPayloads';
import { trackD2CNonSegmentAnalytics } from './trackD2CNonSegmentAnalytics';
import { NonSegmentEvents } from './types';
import UserBrowser from './UserBrowser';

type Aggregate = 'CustomerSession' | 'Customer' | 'ProductOffer';

export function useTrackRoute(aggregate?: Aggregate) {
  const path = usePathname();

  // Accomodate GameStop and Affiliate flows which are using different
  // slug names for partner and product
  const { partner, partnerIdentifier, product, productIdentfier } = useParams();
  const presentation = useParams().presentation ?? getPaths(path).presentation;
  const isSteps = getPaths(path).presentation === 'steps';
  const partnerToUse = partner ?? partnerIdentifier;
  const productToUse = product ?? productIdentfier;

  const searchParams = useSearchParams();
  const currentPathRef = useRef<string>();

  const poid = searchParams.get('poid');

  const [{ data: productOffer }] = useProductOffer({
    productOfferId: poid as string,
    required: false,
  });

  useEffect(() => {
    async function trackAnalytics() {
      let stepsProductOffer: EventPropertiesInput['productOffer'] | undefined;
      let analyticProduct: EventPropertiesInput['products'] | undefined;
      let customerSession:
        | EventPropertiesInput['customerSession']
        | undefined
        | null;
      if (currentPathRef.current !== path) {
        async function getProductOfferData() {
          if (isSteps) {
            const productOfferData = await fetchProductOffer();
            const productConfig = await fetchProductConfig();
            if (productOfferData?.success) {
              stepsProductOffer = {
                id: productOfferData?.data.productOffer.id,
                productEnrollment: productOfferData?.data.productEnrollment,
                productEligibility: productOfferData?.data.productEligibility,
                productSku: productOfferData?.data.productOffer.productSku,
              };
            }
            const browserSession = UserBrowser.get();
            const formData = new FormData();
            formData.append('partner', partnerToUse as string);
            const queryCustomerSessionResult = await queryCustomerSession(
              { success: false },
              formData,
            );
            customerSession = queryCustomerSessionResult.success
              ? {
                  ...queryCustomerSessionResult.data.customerSession,
                  expertSessions: [] as ExpertSession[],
                }
              : undefined;
            analyticProduct = [
              {
                id: stepsProductOffer?.id as string,
                productId: stepsProductOffer?.productSku as string,
                sku: stepsProductOffer?.productSku as string,
                category: productConfig.success
                  ? productConfig.data.config.productType
                  : undefined,
                name: productConfig.success
                  ? productConfig.data.config.retailName
                  : undefined,
                brand: productConfig.success
                  ? productConfig.data.config.internalPartner
                  : undefined,
                price: productConfig.success
                  ? productConfig.data.config.price
                  : undefined,
                quantity: 1,
                coupon: browserSession?.sessionQueryParameters?.coupon,
              },
            ];
          }
        }

        if (!poid && isSteps) {
          await getProductOfferData();
        }

        currentPathRef.current = path;
        const analyticEvent = buildEventProperties({
          event: NonSegmentEvents.RoutePathChanged,
          customerSession,
          customer: customerSession?.customer,
          productOffer: poid ? productOffer : stepsProductOffer,
          products: analyticProduct ?? undefined,
          extraData: {
            productUrlSlug: productToUse ?? '',
            partnerUrlSlug: partnerToUse ?? '',
            presentationUrlSlug: presentation ?? '',
            ...getUrlData(),
            Aggregate: aggregate,
          },
        });

        trackD2CNonSegmentAnalytics(
          NonSegmentEvents.RoutePathChanged,
          analyticEvent,
        );

        trackEventsInFullStory(
          analyticEvent.Name || 'RoutePathChanged',
          analyticEvent,
        );
      }
    }
    trackAnalytics();
  }, [
    path,
    poid,
    aggregate,
    productOffer,
    productToUse,
    partnerToUse,
    presentation,
  ]);
}
